// src/components/Footer.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Important pour les liens internes
import { pb } from '../lib/pocketbase';
import './Footer.css'; // Le fichier CSS du Footer

const Footer = ({ showInstallButton, onInstallClick }) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const data = await pb.collection('informations_contact').getFullList({
          filter: 'actif = true'
        });
        if (data.length > 0) {
          setContactInfo(data[0]); // Prendre le premier enregistrement actif
        }
      } catch (err) {
        console.error('Erreur lors du chargement des informations de contact:', err);
      } finally {
        setLoading(false);
      }
    };

    loadContactInfo();
  }, []);

  const formatHoraire = (horaires, fermetures, jour) => {
    if (fermetures && fermetures[jour]) {
      return 'Fermé';
    }
    return horaires && horaires[jour] ? horaires[jour] : 'Non défini';
  };

  const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  const joursAbrege = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <footer className="footer">
      <div className="footer-content-wrapper">

        {/* Colonne 1: Logo/Nom et Description Courte */}
        <div className="footer-section footer-brand-info">
          <Link to="/" className="footer-logo-link">
            {contactInfo && contactInfo.logo ? (
              <img 
                src={pb.files.getUrl(contactInfo, contactInfo.logo)} 
                alt={`Logo ${contactInfo.nom}`} 
                className="footer-logo" 
              />
            ) : (
              <img src="/images/logo.png" alt="Logo Auberge Peguilhan" className="footer-logo" />
            )}
          </Link>
          <div className="footer-name">
            {contactInfo ? contactInfo.nom : 'Auberge Peguilhan'}
          </div>
          <p className="footer-description">
            Votre havre de paix au cœur des côteaux pyrénéens, pour une expérience authentique et conviviale.
          </p>
          
          {/* Bouton d'installation PWA */}
          {showInstallButton && (
            <button className="install-btn" onClick={onInstallClick}>
              📲 Installer l'application
            </button>
          )}
        </div>

        {/* Colonne 2: Liens Rapides */}
        <div className="footer-section footer-links">
          <h3>Liens Rapides</h3>
          <ul>
            <li><Link to="/a-propos">À Propos</Link></li>
            <li><Link to="/auberge">Notre Auberge</Link></li>
            <li><Link to="/gites">Nos Gîtes</Link></li>
            <li><Link to="/la-ferme">La Ferme</Link></li>
            <li><Link to="/tarifs">Tarifs</Link></li>
            <li><Link to="/notre-region">Notre Région</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>

        {/* Colonne 3: Coordonnées */}
        <div className="footer-section footer-contact-hours">
          <h3>Contact & Infos</h3>
          {contactInfo ? (
            <>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Adresse</div>
                <div className="footer-contact-value">{contactInfo.adresse_complete}</div>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Téléphone</div>
                <div className="footer-contact-value">
                  <a href={`tel:${contactInfo.telephone}`}>{contactInfo.telephone}</a>
                </div>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Email</div>
                <div className="footer-contact-value">
                  <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </div>
              </div>
              
            </>
          ) : (
            <>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Adresse</div>
                <div className="footer-contact-value">
                  {contactInfo?.adresse || 'Adresse non disponible'}
                </div>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Téléphone</div>
                <div className="footer-contact-value">
                  <a href="tel:+33561887578">05 61 88 75 78</a>
                </div>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-label">Email</div>
                <div className="footer-contact-value">
                  <a href="mailto:contact@aubergepeguilhan.com">contact@aubergepeguilhan.com</a>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Colonne 4: Horaires Unifiés */}
        <div className="footer-section footer-hours-detailed">
          <h3>Nos Horaires</h3>
          
          {/* Horaires unifiés */}
          {contactInfo && contactInfo.horaires_restaurant ? (
            <div className="footer-hours-table">
              {jours.map((jour, index) => (
                <div key={jour} className="footer-hours-row">
                  <span className="footer-hours-day">{joursAbrege[index]}</span>
                  <span className={`footer-hours-time ${contactInfo.ferme_restaurant && contactInfo.ferme_restaurant[jour] ? 'ferme' : ''}`}>
                    {formatHoraire(contactInfo.horaires_restaurant, contactInfo.ferme_restaurant, jour)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="footer-hours-table">
              <div className="footer-hours-row"><span className="footer-hours-day">Lun</span><span className="footer-hours-time">09:00–16:00</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Mar</span><span className="footer-hours-time">09:00–16:00</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Mer</span><span className="footer-hours-time ferme">Fermé</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Jeu</span><span className="footer-hours-time">09:00–16:00</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Ven</span><span className="footer-hours-time">09:00–16:00</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Sam</span><span className="footer-hours-time ferme">Fermé</span></div>
              <div className="footer-hours-row"><span className="footer-hours-day">Dim</span><span className="footer-hours-time">09:00–16:00</span></div>
            </div>
          )}

          <div className="footer-social-container">
            <a href="https://www.instagram.com/ferme.auberge.de.peguilhan/" target="_blank" rel="noreferrer" className="social-icon" aria-label="Instagram">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="https://www.facebook.com/people/Ferme-Auberge-de-Peguilhan/100090386873053/" target="_blank" rel="noreferrer" className="social-icon" aria-label="Facebook">
              <i className="fab fa-facebook-f"></i>
            </a>
            <a href={`mailto:${contactInfo ? contactInfo.email : 'contact@aubergepeguilhan.com'}`} className="social-icon" aria-label="Email">
              <i className="fas fa-envelope"></i>
            </a>
            <a href="https://www.tripadvisor.fr" target="_blank" rel="noreferrer" className="social-icon" aria-label="TripAdvisor">
              <i className="fab fa-tripadvisor"></i>
            </a>
          </div>
        </div>

      </div>

      {/* Section inférieure du footer (copyright) */}
      <div className="footer-bottom">
        <p className="footer-copyright">
          &copy; {new Date().getFullYear()} Auberge Peguilhan. Tous droits réservés. |
          <Link to="/mentions-legales" className="legal-link"> Mentions Légales</Link> |
          <Link to="/politique-de-confidentialite" className="legal-link"> Politique de Confidentialité</Link>
        </p>
      </div>
    </footer>
  );
};

export default Footer;